/**
 * Browser-locale detection and shared current-locale state for the players.
 *
 * The standalone players ship `en` and `fr` message catalogs; anything else
 * falls back to French (the platform default). Keeping the detection and the
 * reactive current locale here makes them the single source of truth used by
 * every player i18n composable and by the standalone embed entry to set
 * `<html lang>`, so the document language always matches the locale the player
 * actually displays.
 *
 * The current locale is an INPUT: it defaults to the browser language (correct
 * for public embeds), but callers can override it via `setPlayerLocale` — e.g.
 * the in-app SPA syncs it with the active vue-i18n locale (the user's chosen
 * language), and the standalone `mount()` can take an explicit `locale` option.
 * The players themselves stay decoupled from vue-i18n.
 */

import { ref, type Ref } from 'vue'

export const PLAYER_SUPPORTED_LOCALES = ['en', 'fr'] as const

export type PlayerLocale = (typeof PLAYER_SUPPORTED_LOCALES)[number]

export const PLAYER_DEFAULT_LOCALE: PlayerLocale = 'en'

/**
 * Resolve the player UI locale from the visitor's browser language.
 *
 * Returns one of the supported locales, falling back to the English default for
 * unsupported languages or when `navigator` is unavailable (SSR).
 */
export function detectBrowserLocale(): PlayerLocale {
  if (typeof navigator !== 'undefined' && navigator.language) {
    const lang = navigator.language.split('-')[0]?.toLowerCase()
    if (lang && (PLAYER_SUPPORTED_LOCALES as readonly string[]).includes(lang)) {
      return lang as PlayerLocale
    }
  }
  return PLAYER_DEFAULT_LOCALE
}

/**
 * Reactive current player locale, shared by every player i18n composable.
 *
 * Initialized from the browser language; override it via `setPlayerLocale`.
 * Typed as `Ref<string>` so the per-player i18n composables can expose it
 * directly without locale-union variance friction.
 */
export const currentLocale: Ref<string> = ref<string>(detectBrowserLocale())

/**
 * Set the shared player locale.
 *
 * Accepts a raw locale string (e.g. "fr-FR"); unsupported languages are
 * ignored, keeping the previous value. Returns whether the locale was applied.
 */
export function setPlayerLocale(locale: string | null | undefined): boolean {
  const normalized = locale?.split('-')[0]?.toLowerCase()
  if (normalized && (PLAYER_SUPPORTED_LOCALES as readonly string[]).includes(normalized)) {
    currentLocale.value = normalized
    return true
  }
  return false
}
